import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { submissions, users, products as productsApi, brokers as brokersApi } from '../services/api.js';
import { UnderwriterChat } from '../components/UnderwriterChat.jsx';
import './UnderwriterDashboard.css';

function getRiskBadgeClass(score) {
  if (score == null) return 'badge';
  if (score <= 2) return 'badge badge--red';
  if (score <= 5) return 'badge badge--orange';
  if (score <= 8) return 'badge badge--yellow';
  return 'badge badge--green';
}

function getRiskLabel(score) {
  if (score == null) return 'N/A';
  if (score <= 2) return 'High';
  if (score <= 5) return 'Elevated';
  if (score <= 8) return 'Moderate';
  return 'Low';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In Review' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'bound', label: 'Bound' },
  { value: 'declined', label: 'Declined' },
  { value: 'referred', label: 'Referred' },
];

const RISK_TIER_OPTIONS = [
  { value: '', label: 'All Risk Tiers' },
  { value: '0-2', label: 'High (0–2)' },
  { value: '3-5', label: 'Elevated (3–5)' },
  { value: '6-8', label: 'Moderate (6–8)' },
  { value: '9-10', label: 'Low (9–10)' },
];

export function UnderwriterDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productList, setProductList] = useState([]);
  const [uwList, setUwList] = useState([]);
  const [brokerList, setBrokerList] = useState([]);

  // Filters
  const [filterProduct, setFilterProduct] = useState('');
  const [filterRiskTier, setFilterRiskTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUw, setFilterUw] = useState('');

  // Workload
  const [workload, setWorkload] = useState(null);

  // New submission form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ insuredName: '', productId: '', brokerId: '' });
  const [creating, setCreating] = useState(false);

  const handleCreateSubmission = async () => {
    if (!newForm.insuredName || !newForm.productId || !newForm.brokerId) return;
    setCreating(true);
    try {
      const result = await submissions.create({
        insuredName: newForm.insuredName,
        productId: newForm.productId,
        brokerId: newForm.brokerId,
        source: 'manual',
      });
      const subId = result?.data?.submissionId || result?.submissionId;
      setShowNewForm(false);
      setNewForm({ insuredName: '', productId: '', brokerId: '' });
      if (subId) {
        navigate(`/submissions/${subId}`);
      } else {
        loadSubmissions();
      }
    } catch (err) {
      alert('Failed to create submission: ' + (err.message || 'Unknown error'));
    } finally {
      setCreating(false);
    }
  };

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterProduct) params.product_id = filterProduct;
      if (filterStatus) params.status = filterStatus;
      if (filterUw) params.assigned_uw = filterUw;
      if (filterRiskTier) params.risk_tier = filterRiskTier;

      const result = await submissions.list(params);
      setData(result.data || result.submissions || result.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [filterProduct, filterStatus, filterUw, filterRiskTier]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  useEffect(() => {
    async function loadFilters() {
      try {
        const [prods, uwUsers, brokersRes] = await Promise.allSettled([
          productsApi.list(),
          users.list({ role: 'underwriter' }),
          brokersApi.list(),
        ]);
        if (prods.status === 'fulfilled') {
          const items = prods.value.data || prods.value.products || prods.value.items || [];
          setProductList(Array.isArray(items) ? items : []);
        }
        if (uwUsers.status === 'fulfilled') {
          const items = uwUsers.value.data || uwUsers.value.users || uwUsers.value.items || [];
          setUwList(Array.isArray(items) ? items : []);
        }
        if (brokersRes.status === 'fulfilled') {
          const items = brokersRes.value.data || brokersRes.value || [];
          setBrokerList(Array.isArray(items) ? items : []);
        }
      } catch {
        // Filters are non-critical
      }
    }
    loadFilters();
  }, []);

  useEffect(() => {
    async function loadWorkload() {
      if (!user?.userId) return;
      try {
        const result = await users.getWorkload(user.userId);
        setWorkload(result.data || result);
      } catch {
        // Non-critical
      }
    }
    loadWorkload();
  }, [user?.userId]);

  const currentCount = workload?.current || workload?.active_count || 0;
  const maxCount = workload?.max || workload?.capacity || 25;
  const capacityPct = Math.min((currentCount / maxCount) * 100, 100);
  const capacityColor =
    capacityPct >= 90 ? 'var(--color-risk-red)' :
    capacityPct >= 70 ? 'var(--color-risk-orange)' :
    capacityPct >= 50 ? 'var(--color-risk-yellow)' :
    'var(--color-risk-green)';

  return (
    <div className="uw-dashboard">
      <div className="uw-dashboard__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <div>
            <h1 className="uw-dashboard__title">Submission Queue</h1>
            <p className="uw-dashboard__subtitle">
              {Array.isArray(data) ? data.length : 0} submission{data.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            className="btn btn--primary"
            onClick={() => setShowNewForm(!showNewForm)}
          >
            + New Submission
          </button>
        </div>
        <div className="uw-dashboard__workload card">
          <div className="workload__label">
            <span className="workload__title">My Workload</span>
            <span className="workload__count">{currentCount} / {maxCount}</span>
          </div>
          <div className="workload__bar">
            <div
              className="workload__fill"
              style={{ width: `${capacityPct}%`, backgroundColor: capacityColor }}
            />
          </div>
          <span className="workload__hint">
            {maxCount - currentCount} slots available
          </span>
        </div>
      </div>

      {showNewForm && (
        <div className="card" style={{ marginBottom: 'var(--space-md)', padding: 'var(--space-lg)' }}>
          <h3 style={{ marginBottom: 'var(--space-md)', fontSize: '16px' }}>Create New Submission</h3>
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Insured Name *</label>
              <input
                type="text"
                value={newForm.insuredName}
                onChange={(e) => setNewForm((f) => ({ ...f, insuredName: e.target.value }))}
                placeholder="e.g., Acme Cloud Technologies"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: '14px' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Product *</label>
              <select
                value={newForm.productId}
                onChange={(e) => setNewForm((f) => ({ ...f, productId: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: '14px' }}
              >
                <option value="">Select product...</option>
                {productList.map((p) => (
                  <option key={p.ProductID || p.productId} value={p.ProductID || p.productId}>
                    {p.Name || p.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Broker *</label>
              <select
                value={newForm.brokerId}
                onChange={(e) => setNewForm((f) => ({ ...f, brokerId: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: '14px' }}
              >
                <option value="">Select broker...</option>
                {brokerList.map((b) => (
                  <option key={b.BrokerID || b.brokerId} value={b.BrokerID || b.brokerId}>
                    {b.Name || b.name} ({b.Type || b.type})
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button
                className="btn btn--primary"
                onClick={handleCreateSubmission}
                disabled={creating || !newForm.insuredName || !newForm.productId || !newForm.brokerId}
              >
                {creating ? 'Creating...' : 'Create & Open'}
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => setShowNewForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="uw-dashboard__filters card">
        <select
          value={filterProduct}
          onChange={(e) => setFilterProduct(e.target.value)}
          className="uw-filter__select"
        >
          <option value="">All Products</option>
          {productList.map((p) => (
            <option key={p.ProductID || p.productId || p.id} value={p.ProductID || p.productId || p.id}>
              {p.Name || p.name || p.product_name}
            </option>
          ))}
        </select>

        <select
          value={filterRiskTier}
          onChange={(e) => setFilterRiskTier(e.target.value)}
          className="uw-filter__select"
        >
          {RISK_TIER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="uw-filter__select"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          value={filterUw}
          onChange={(e) => setFilterUw(e.target.value)}
          className="uw-filter__select"
        >
          <option value="">All Underwriters</option>
          {uwList.map((u) => (
            <option key={u.UnderwriterID || u.underwriterId || u.id} value={u.UnderwriterID || u.underwriterId || u.id}>
              {u.Name || u.name || u.display_name || u.Email || u.email}
            </option>
          ))}
        </select>

        <button className="btn btn--secondary" onClick={loadSubmissions}>
          Refresh
        </button>
      </div>

      {error && (
        <div className="uw-dashboard__error">
          <p>{error}</p>
          <button className="btn btn--secondary" onClick={loadSubmissions}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="uw-dashboard__loading">
          <div className="spinner" />
          <p>Loading submissions...</p>
        </div>
      ) : (
        <div className="uw-dashboard__table-wrap card">
          <table className="uw-table">
            <thead>
              <tr>
                <th>Insured</th>
                <th>Product</th>
                <th>Broker</th>
                <th>Risk Score</th>
                <th>Status</th>
                <th>Assigned UW</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data) && data.length > 0 ? (
                data.map((sub) => {
                  const id = sub.SubmissionID || sub.submissionId || sub.submission_id || sub.id;
                  const riskScore = sub.RiskScore ?? sub.riskScore ?? sub.risk_score;
                  const status = sub.Status || sub.status || 'new';
                  return (
                    <tr
                      key={id}
                      className="uw-table__row"
                      onClick={() => navigate(`/submissions/${id}`)}
                    >
                    <td className="uw-table__insured">
                      {sub.InsuredName || sub.insuredName || '—'}
                    </td>
                    <td>{sub.ProductName || sub.productName || sub.ProductID || '—'}</td>
                    <td>{sub.BrokerName || sub.brokerName || sub.BrokerID || '—'}</td>
                      <td>
                        <span className={getRiskBadgeClass(riskScore)}>
                          {riskScore != null && riskScore >= 0 ? `${riskScore} · ${getRiskLabel(riskScore)}` : 'Pending'}
                        </span>
                      </td>
                      <td>
                        <span className="uw-status-chip" data-status={status}>
                          {status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>{sub.AssignedUWName || sub.assignedUWName || sub.AssignedUW || '—'}</td>
                      <td className="uw-table__date">
                        {formatDate(sub.CreatedAt || sub.createdAt || sub.created_at)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="uw-table__empty">
                    No submissions found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <UnderwriterChat />
    </div>
  );
}
